import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { AiOutlineDelete } from 'react-icons/ai';
import AdminLayout from './AdminLayout.jsx';
import Loader from '../common/Loader.jsx';
import Table from '../common/Table.jsx';
import { AddEquityGrant, DeleteEquityGrant, ListEquityGrants } from '../store/slices/equity-grants.js';
import { ListMembers } from '../store/slices/members.js';

const EquityGrantList = () => {
  const dispatch = useDispatch();
  const tableRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user'));
  const profileId = user?.data?.profile_id;

  const [isLoading, setIsLoading] = useState(false);
  const [equityGrants, setEquityGrants] = useState([]);
  const [members, setMembers] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [grantForm, setGrantForm] = useState({ grant_received: '', date_received: '' });
  const [formError, setFormError] = useState('');

 const [columns] = useState([
    { header: 'Name', field: 'member_name' },
    { header: 'Email', field: 'email' },
    { header: 'Folio No', field: 'folio_no' },
    { header: 'Date', field: 'added_on' },
    { header: 'Grant (Amount / Date)', field: 'grant_display' },
    {
      header: 'Actions',
      field: 'actions',
      isAction: true,
      actionButtons: [
        { name: 'Add Grant', className: 'btn-default' },
      ],
    },
  ]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleOpenModal = (member = null) => {
    setGrantForm({ grant_received: '', date_received: '' });
    setFormError('');
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const getMemberId = (member) => member?.id || member?.member_id;

  const handleCloseModal = () => {
    setFormError('');
    setIsModalOpen(false);
  };

  const fetchEquityGrants = (memberId) => {
    const payload = { profile_id: profileId };
    if (memberId) payload.member_id = memberId; // avoid sending undefined
    setIsLoading(true);
    dispatch(ListEquityGrants(payload))
      .unwrap()
      .then((data) => {
        setIsLoading(false);
        setEquityGrants((prev = []) => {
          if (!memberId) return data?.data || [];
          const remaining = (prev || []).filter((item) => item?.member_id !== memberId);
          return [...remaining, ...(data?.data || [])];
        });
      })
      .catch(({ message }) => {
        setIsLoading(false);
        toast.error(message || 'Unable to load equity grants');
      });
  };

  const handleDeleteGrant = useCallback(
    (grant) => {
      const grantId = grant?.id;
      if (!grantId) {
        toast.error('Unable to delete equity grant');
        return;
      }

      setIsLoading(true);
      dispatch(DeleteEquityGrant({ id: grantId, profile_id: profileId, member_id: grant?.member_id }))
        .unwrap()
        .then((response) => {
          toast.success(response?.message || 'Equity grant deleted successfully');
          setEquityGrants((prev) => (prev || []).filter((item) => item.id !== grantId));
        })
        .catch(({ message }) => {
          toast.error(message || 'Unable to delete equity grant');
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, profileId],
  );

  const fetchEquityGrantsForMembers = async (membersList) => {
    if (!Array.isArray(membersList) || membersList.length === 0) return;
    setIsLoading(true);
    try {
      const results = await Promise.all(
        membersList.map((member) => {
          const memberId = getMemberId(member);
          if (!memberId) return null;
          return dispatch(ListEquityGrants({ profile_id: profileId, member_id: memberId }))
            .unwrap()
            .then((res) => res?.data || [])
            .catch(() => null);
        }),
      );

      const merged = results
        .filter(Boolean)
        .flat();
      setEquityGrants(merged);
    } catch (err) {
      toast.error('Unable to load equity grants');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMembers = () => {
    setIsLoading(true);
    dispatch(ListMembers({ profile_id: profileId }))
      .unwrap()
      .then((data) => {
        setIsLoading(false);
        setMembers(data?.data || []);
        fetchEquityGrantsForMembers(data?.data || []);
      })
      .catch(({ message }) => {
        setIsLoading(false);
        setMembers([]);
        toast.error(message || 'Unable to load members');
      });
  };

  useEffect(() => {
    if (profileId) {
      fetchMembers();
    }
  }, [profileId]);

  const saveGrant = () => {
    const memberId = getMemberId(selectedMember);
    if (!grantForm.date_received || !grantForm.grant_received) {
      setFormError('Date and amount are required.');
      return;
    }

    if (!memberId) {
      setFormError('Select a member by clicking Add Grant.');
      return;
    }
    console.log('Saving grant for member:', memberId, 'with data:', grantForm, profileId);
    setIsLoading(true);
    dispatch(
      AddEquityGrant({
        member_id: memberId,
        profile_id: profileId,
        grant_received: grantForm.grant_received,
        date_received: grantForm.date_received,
      }),
    )
      .unwrap()
      .then((response) => {
        toast.success(response?.message || 'Equity grant added successfully');
        setIsModalOpen(false);
        fetchEquityGrants(memberId);
      })
      .catch(({ message }) => {
        setIsLoading(false);
        toast.error(message || 'Unable to add equity grant');
      });
  };

  const grantsByMember = useMemo(() => {
    const map = {};
    (equityGrants || []).forEach((grant) => {
      const id = grant?.member_id;
      if (!id) return;
      if (!map[id]) map[id] = [];
      map[id].push(grant);
    });

    Object.values(map).forEach((list) => {
      list.sort((a, b) => {
        const dateA = new Date(a?.date_received || a?.created_at || 0);
        const dateB = new Date(b?.date_received || b?.created_at || 0);
        return dateB - dateA;
      });
    });

    return map;
  }, [equityGrants]);

  const membersWithGrants = useMemo(() => {
    return (members || []).map((member) => {
      const memberId = getMemberId(member);
      const grants = grantsByMember[memberId] || [];

      const grantDisplayText = grants
        .map((g) => [g?.grant_received, g?.date_received].filter(Boolean).join(' / '))
        .filter(Boolean)
        .join(' | ');

      const grantDisplay = grants.length
        ? (
            <div className="d-flex flex-column gap-1">
              {grants.map((grant) => {
                const label = [grant?.grant_received, grant?.date_received]
                  .filter(Boolean)
                  .join(' / ');
                return (
                  <div
                    key={`${memberId}-${grant?.id || label}`}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <span>{label}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0 ms-2"
                      aria-label="Delete grant"
                      title="Delete grant"
                      onClick={() => handleDeleteGrant(grant)}
                    >
                      <AiOutlineDelete size={18} />
                    </button>
                  </div>
                );
              })}
            </div>
          )
        : '';

      const latestGrant = grants[0] || {};

      return {
        ...member,
        grant_received: latestGrant?.grant_received || '',
        date_received: latestGrant?.date_received || '',
        grant_display: grantDisplay,
        grant_search_text: grantDisplayText,
      };
    });
  }, [members, grantsByMember, handleDeleteGrant]);

  const filteredMembers = membersWithGrants?.filter((member) => {
    const query = (searchQuery || '').toLowerCase();
    const normalize = (value) => (value ?? '').toString().toLowerCase();

    return (
      normalize(member?.member_name).includes(query) ||
      normalize(member?.email).includes(query) ||
      normalize(member?.folio_no).includes(query) ||
      normalize(member?.grant_received).includes(query) ||
      normalize(member?.date_received).includes(query) ||
      normalize(member?.grant_search_text).includes(query)
    );
  });

  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="row content-body">
        <div className="container-fluid">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">Equity Grant</h2>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#">Registers</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Equity Grant List
                </li>
              </ol>
            </div>
            <div className="d-flex justify-content-end" />
          </div>

          <div className="row">
            <div className="col-md-12">
              <Table
                columns={columns}
                data={filteredMembers}
                tableRef={tableRef}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                totalCount={filteredMembers?.length || 0}
                onPageChange={handlePageChange}
                handleSearchChange={handleSearchChange}
                handleEdit={(member) => handleOpenModal(member)}
              />
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Equity Grant</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                {formError && <div className="alert alert-danger">{formError}</div>}
                <div className="form-group">
                  <label>Date Received</label>
                  <input
                    type="date"
                    className="form-control"
                    value={grantForm.date_received}
                    onChange={(e) => setGrantForm({ ...grantForm, date_received: e.target.value })}
                  />
                </div>
                <div className="form-group mt-3">
                  <label>Grant Amount</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={grantForm.grant_received}
                    onChange={(e) => setGrantForm({ ...grantForm, grant_received: e.target.value })}
                    placeholder="Enter amount"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn ripple btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="button" className="btn ripple btn-default" onClick={saveGrant}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default EquityGrantList;
